import { Button, Dialog } from '@platform/design-system';
import { useTranslation } from '@platform/i18n';
import { useEffect, useRef, type ComponentProps, type ReactNode } from 'react';

type SubmitHandler = NonNullable<ComponentProps<'form'>['onSubmit']>;

export type FormSheetProps = {
  open: boolean;
  title: string;
  formId: string;
  submitting: boolean;
  submitLabel?: string;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: SubmitHandler;
  children: ReactNode;
};

export const FormSheet = ({
  open,
  title,
  formId,
  submitting,
  submitLabel,
  errorMessage,
  onClose,
  onSubmit,
  children,
}: FormSheetProps) => {
  const { t } = useTranslation();
  const alertRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    alertRef.current?.scrollIntoView({ block: 'nearest' });
  }, [errorMessage]);

  return (
    <Dialog
      open={open}
      variant="side"
      title={title}
      closeLabel={t('common.close')}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button form={formId} type="submit" disabled={submitting}>
            {submitting ? t('common.saving') : (submitLabel ?? t('common.save'))}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        noValidate
        onSubmit={(event) => {
          event.stopPropagation();
          onSubmit(event);
        }}
      >
        {errorMessage === null ? null : (
          <p ref={alertRef} role="alert" data-testid={`${formId}-error`}>
            {errorMessage}
          </p>
        )}
        {children}
      </form>
    </Dialog>
  );
};
